import { useEffect, useState } from "react";
import { CheckCircle2, Circle, Loader2, MailPlus, Sunrise, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ListenLine } from "./ListenLine";

// The long reads worth a Saturday morning, with a tick for each one you finish.
export const WeekendList = ({ lang, speaker }) => {
  const [state, setState] = useState({ loading: true, stories: [], minutes: 0, read: 0 });
  const [saturday, setSaturday] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/news/weekend", { params: lang ? { lang } : {} });
      setState({ loading: false, ...data });
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
      toast.error(apiError(e.response?.data?.detail) || "Could not gather your long reads");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [lang]);

  useEffect(() => {
    api.get("/news/weekend/email").then(({ data }) => setSaturday(data.on)).catch(() => {});
  }, []);

  const toggleSaturday = async () => {
    const on = !saturday;
    try {
      await api.put("/news/weekend/email", { on });
      setSaturday(on);
      toast.success(on ? "MiLu will post your list on Saturday mornings"
                       : "Saturday email switched off");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not change that");
    }
  };

  const sendPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/news/weekend/email/preview");
      setPreview(data);
      toast[data.sent ? "success" : "error"](
        data.sent ? `Sent to ${data.to} — it's in your inbox`
                  : data.reason || "MiLu could not post it, but here's how it looks",
        { duration: data.sent ? 5000 : 9000 });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not build that preview");
    } finally {
      setBusy(false);
    }
  };

  const tick = async (story) => {
    const done = !story.done;
    setState((s) => ({
      ...s,
      stories: s.stories.map((r) => (r.story_id === story.story_id ? { ...r, done } : r)),
      read: s.read + (done ? 1 : -1),
    }));
    try {
      await api.post("/news/weekend/done", { story_id: story.story_id, done });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not tick that off");
      load();
    }
  };

  return (
    <div className="mt-6 space-y-3" data-testid="news-weekend">
      <div className="flex flex-wrap items-center gap-2">
        <Sunrise className="h-4 w-4 text-brand" />
        <p className="text-sm text-ink-soft">
          The long reads from your feed and your saved list — about {state.minutes} minutes in all
          {state.carried ? `, ${state.carried} carried over from last week` : ""}.
        </p>
        {state.stories.length > 0 && (
          <span className="ms-auto text-xs font-semibold text-brand" data-testid="weekend-progress">
            {state.read} of {state.stories.length} read
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="weekend-email-row">
        <label className="inline-flex min-h-[32px] cursor-pointer items-center gap-2 text-xs text-ink-soft">
          <input
            type="checkbox"
            data-testid="weekend-email-toggle"
            checked={saturday}
            onChange={toggleSaturday}
            className="h-3.5 w-3.5 accent-[var(--theme)]"
          />
          Post my weekend list on Saturday mornings
        </label>
        <button
          data-testid="weekend-email-preview"
          onClick={sendPreview}
          disabled={busy}
          className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-white/10 px-3 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-ink disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <MailPlus className="h-3.5 w-3.5 text-brand" />}
          Send me a preview
        </button>
      </div>

      {preview && (
        <div className="rounded-2xl border border-white/10 bg-panel p-4" data-testid="weekend-preview">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold" dir="auto">{preview.subject}</p>
            <span className="text-xs text-ink-soft">
              {preview.sent ? `Sent to ${preview.to}` : "Not posted — preview only"}
            </span>
            <button data-testid="weekend-preview-close" onClick={() => setPreview(null)}
                    className="ms-auto rounded-full p-1 text-ink-soft hover:text-ink">
              <X className="h-4 w-4" />
            </button>
          </div>
          <iframe
            title="Saturday email preview"
            data-testid="weekend-preview-frame"
            srcDoc={preview.html}
            className="mt-3 h-[420px] w-full rounded-xl border border-white/10 bg-black"
          />
        </div>
      )}

      {state.loading && (
        <p className="flex items-center gap-2 text-sm text-ink-soft">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Gathering your long reads…
        </p>
      )}

      {!state.loading && state.stories.length === 0 && (
        <p className="text-sm text-ink-soft" data-testid="weekend-empty">
          No long reads waiting — everything in your feed is a quick one today.
        </p>
      )}

      {state.stories.map((story) => (
        <div key={story.story_id}
             className={`flex items-start gap-3 rounded-2xl border p-4 transition-colors ${
               story.done ? "border-brand/30 bg-brand/5" : "border-white/10 bg-panel"}`}
             data-testid={`weekend-story-${story.story_id}`}>
          <button
            data-testid={`weekend-tick-${story.story_id}`}
            onClick={() => tick(story)}
            aria-label={story.done ? "Mark as unread" : "Mark as read"}
            className="mt-0.5 shrink-0 text-ink-soft transition-colors hover:text-brand"
          >
            {story.done ? <CheckCircle2 className="h-5 w-5 text-brand" />
                        : <Circle className="h-5 w-5" />}
          </button>
          <div className="min-w-0">
            <a href={story.url} target="_blank" rel="noreferrer" dir="auto"
               className={`text-base font-semibold hover:text-brand ${
                 story.done ? "text-ink-soft line-through decoration-brand/40" : ""}`}>
              {story.title}
            </a>
            <p className="mt-1 text-xs text-ink-soft">
              {story.carried && (
                <span className="me-1.5 rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-semibold text-brand"
                      data-testid={`carried-${story.story_id}`}>
                  from last week
                </span>
              )}
              {story.source}
              <span className="text-brand">
                {` · ${story.read_est ? "~" : ""}${story.read_min} min read`}
              </span>
              {story.saved ? " · saved" : ""}
              <ListenLine story={story} speaker={speaker}
                          testid={`weekend-listen-${story.story_id}`} />
            </p>
            {story.explain && (
              <p className="mt-2 text-xs leading-snug text-ink-soft" dir="auto">{story.explain}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
